import React, { useState, memo } from 'react';
import PropTypes from 'prop-types';

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN || 'pk.eyJ1IjoibWl0aHJpbC1tZWRpYSIsImEiOiJjazhvb211cWIxYjZhM2Vxc3NnZm9hMmJzIn0.qMq9yOzeUkajyvm1AfTk7Q';

/**
 * MiniMap component for displaying a small static map with a marker
 * Uses Mapbox Static Images API for better performance and reliability
 * 
 * @param {Object} props - Component props
 * @param {Object} props.coordinates - Location coordinates
 * @param {number} props.coordinates.lat - Latitude
 * @param {number} props.coordinates.lng - Longitude
 * @param {string} props.className - Additional CSS classes
 * @param {string} props.mapStyle - Mapbox style (defaults to streets-v12)
 * @returns {JSX.Element} - MiniMap component
 */
const MiniMap = memo(({ coordinates, className = '', mapStyle = 'streets-v12' }) => {
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Default to Seattle coordinates if none provided
  const lat = coordinates?.lat || 47.6062;
  const lng = coordinates?.lng || -122.3321;
  
  // Mapbox Static Images API URL
  // Format: https://api.mapbox.com/styles/v1/{username}/{style_id}/static/{overlay}/{lon},{lat},{zoom}/{width}x{height}{@2x}?access_token={access_token}
  const zoom = 10;
  const width = 400;
  const height = 300;
  const markerColor = 'f97316'; // Brand orange color (without #)
  
  // Create marker overlay: pin-s for small pin, + for color
  const overlay = `pin-s+${markerColor}(${lng},${lat})`;
  
  const staticMapUrl = `https://api.mapbox.com/styles/v1/mapbox/${mapStyle}/static/${overlay}/${lng},${lat},${zoom}/${width}x${height}@2x?access_token=${MAPBOX_TOKEN}`;

  const handleLoad = () => {
    setIsLoading(false);
    setHasError(false);
  };

  const handleError = () => {
    setIsLoading(false);
    setHasError(true);
  };

  if (hasError) {
    return (
      <div className={`overflow-hidden shadow-sm h-full bg-gray-100 flex items-center justify-center ${className}`}>
        <div className="text-gray-400 text-xs">Map unavailable</div>
      </div>
    );
  }

  return (
    <div className={`overflow-hidden shadow-sm h-full relative ${className}`}>
      {isLoading && (
        <div className="absolute inset-0 bg-gray-100 flex items-center justify-center z-10">
          <div className="animate-pulse text-gray-400 text-xs">Loading map...</div>
        </div>
      )}
      <img
        src={staticMapUrl}
        alt={`Map showing location at ${lat}, ${lng}`}
        className="w-full h-full object-cover"
        onLoad={handleLoad}
        onError={handleError}
        loading="lazy"
      />
    </div>
  );
});

MiniMap.displayName = 'MiniMap';

MiniMap.propTypes = {
  coordinates: PropTypes.shape({
    lat: PropTypes.number,
    lng: PropTypes.number,
  }),
  className: PropTypes.string,
  mapStyle: PropTypes.string,
};

export default MiniMap;
